import React, { useState } from 'react';
import { Icon } from '../../Icon';
import { parseBrazilianNumber } from '../../../utils/formatUtils';
import { FormField, ResultCard, EducationalBlock } from '../finance/shared';

const PriceComparisonCalculator = ({ onResultChange }: { onResultChange: (data: { label: string, value: string }[]) => void }) => {
  const [price1, setPrice1] = useState('10,00');
  const [qty1, setQty1] = useState('500');
  const [price2, setPrice2] = useState('18,00');
  const [qty2, setQty2] = useState('1000');
  const [resultState, setResultState] = useState<any>(null);

  const calculate = () => {
    const p1 = parseFloat(parseBrazilianNumber(price1)) || 0;
    const q1 = parseFloat(parseBrazilianNumber(qty1)) || 0;
    const p2 = parseFloat(parseBrazilianNumber(price2)) || 0;
    const q2 = parseFloat(parseBrazilianNumber(qty2)) || 0;
    if (p1 <= 0 || q1 <= 0 || p2 <= 0 || q2 <= 0) return;

    const unit1 = p1 / q1;
    const unit2 = p2 / q2;
    const ratio = unit1 < unit2 ? (1 - (unit1 / unit2)) * 100 : (1 - (unit2 / unit1)) * 100;
    const best = unit1 < unit2 ? 1 : 2;

    const res = { unit1, unit2, best, ratio };
    setResultState(res);
    onResultChange([
      { label: 'Unitário Produto 1', value: `R$ ${unit1.toLocaleString('pt-BR', { minimumFractionDigits: 4 })}` },
      { label: 'Unitário Produto 2', value: `R$ ${unit2.toLocaleString('pt-BR', { minimumFractionDigits: 4 })}` },
      { label: 'Melhor Opção', value: `Produto ${best} (${ratio.toFixed(1)}% mais barato)` }
    ]);
  };

  return (
    <div className="space-y-8">
      <div className="bg-theme-result p-8 rounded-3xl shadow-lg border border-theme-border">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          <div className="space-y-4 p-6 bg-theme-input rounded-2xl border border-theme-border">
            <h4 className="font-black text-xs uppercase tracking-widest text-theme-muted">Produto 1</h4>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Preço (R$)" value={price1} onChange={setPrice1} isNumeric required type="text" />
              <FormField label="Qtd (g/ml)" value={qty1} onChange={setQty1} isNumeric required type="text" />
            </div>
          </div>
          <div className="space-y-4 p-6 bg-theme-input rounded-2xl border border-theme-border">
            <h4 className="font-black text-xs uppercase tracking-widest text-theme-muted">Produto 2</h4>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Preço (R$)" value={price2} onChange={setPrice2} isNumeric required type="text" />
              <FormField label="Qtd (g/ml)" value={qty2} onChange={setQty2} isNumeric required type="text" />
            </div>
          </div>
        </div>

        <div className="flex justify-center mb-8">
          <button
            onClick={calculate}
            className="px-12 py-4 bg-accent-gradient text-white rounded-xl font-bold hover:shadow-accent-hover hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
          >
            <Icon name="play" className="w-5 h-5" />
            Comparar Preços
          </button>
        </div>

        {resultState && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ResultCard title="Custo Unitário 1" value={`R$ ${resultState.unit1.toLocaleString('pt-BR', { minimumFractionDigits: 4 })}`} color="slate" />
            <ResultCard title="Custo Unitário 2" value={`R$ ${resultState.unit2.toLocaleString('pt-BR', { minimumFractionDigits: 4 })}`} color="slate" />
            <ResultCard title="Economia" value={`${resultState.ratio.toFixed(1)}%`} description={`O Produto ${resultState.best} é mais vantajoso`} color="emerald" />
          </div>
        )}
      </div>

      <EducationalBlock
        title="Dica de Consumo Inteligente"
        tips={[
          'Nem sempre a embalagem maior é a mais barata proporcionalmente',
          'Calcule o preço por unidade (g, ml, kg) para comparar marcas diferentes',
          'Fique atento a promoções do tipo "leve 3 pague 2"',
          'Considere a durabilidade e o desperdício ao comprar em grandes quantidades'
        ]}
      />
    </div>
  );
};

export default PriceComparisonCalculator;
